import { Injectable } from '@angular/core';
import {
  EDateRangeType,
  IDateRangeBounds,
  IProfileDateFilterState,
} from '@shared/components/profile-date-filter/profile-date-filter.interface';
import { calculatePredefinedDateRange } from '@shared/components/profile-date-filter/profile-date-filter.util';
import { endOfDay, startOfDay } from 'date-fns';

export type TProfileDateFilterEntityType = 'campaign' | 'creator' | 'creatorDeliverables' | 'customer';

interface IStoredProfileDateFilterState {
  endDate: string | null;
  entityId: string;
  entityType: TProfileDateFilterEntityType;
  selectedRange: EDateRangeType;
  startDate: string | null;
  userId: string;
}

export interface IProfileDateFilterStoredState {
  endDate: Date | null;
  entityId: string;
  entityType: TProfileDateFilterEntityType;
  selectedRange: EDateRangeType;
  startDate: Date | null;
  userId: string;
}

export interface IProfileDateFilterResolvedState {
  endDate: Date | null;
  filterState: IProfileDateFilterState | null;
  selectedRange: EDateRangeType;
  startDate: Date | null;
}

export interface IResolveProfileDateFilterStateParams {
  defaultEndDate: Date | null;
  defaultSelectedRange: EDateRangeType;
  defaultStartDate: Date | null;
  entityId: string | null;
  entityType: TProfileDateFilterEntityType;
  hideAllTimeRangeOption: boolean;
  userId: string | null;
}

export interface IPersistProfileDateFilterStateParams {
  endDate: Date | null;
  entityId: string | null;
  entityType: TProfileDateFilterEntityType;
  selectedRange: EDateRangeType;
  startDate: Date | null;
  userId: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class ProfileDateFilterStateService {
  private readonly storage: Storage | null = typeof window === 'undefined' ? null : window.localStorage;
  private readonly storagePrefix = 'profile-date-filter-state';

  public clearState(entityType: TProfileDateFilterEntityType, entityId: string, userId: string): void {
    if (!this.storage) {
      return;
    }

    const key = this.buildKey(entityType, entityId, userId);
    this.storage.removeItem(this.buildStorageKey(key));
  }

  public getState(
    entityType: TProfileDateFilterEntityType,
    entityId: string,
    userId: string,
  ): IProfileDateFilterStoredState | null {
    if (!this.storage) {
      return null;
    }

    const key = this.buildKey(entityType, entityId, userId);
    const rawState = this.storage.getItem(this.buildStorageKey(key));

    if (!rawState) {
      return null;
    }

    const parsedState = this.safeParse(rawState);

    if (!this.isStoredProfileDateFilterState(parsedState)) {
      return null;
    }

    return {
      endDate: this.parseDate(parsedState.endDate),
      entityId: parsedState.entityId,
      entityType: parsedState.entityType,
      selectedRange: parsedState.selectedRange,
      startDate: this.parseDate(parsedState.startDate),
      userId: parsedState.userId,
    };
  }

  public persistState(params: IPersistProfileDateFilterStateParams): void {
    const { endDate, entityId, entityType, selectedRange, startDate, userId } = params;

    if (!entityId || !userId) {
      return;
    }

    this.setState({
      endDate: endDate ? endOfDay(endDate) : null,
      entityId,
      entityType,
      selectedRange,
      startDate: startDate ? startOfDay(startDate) : null,
      userId,
    });
  }

  public resolveState(params: IResolveProfileDateFilterStateParams): IProfileDateFilterResolvedState {
    const normalizedDefaultSelectedRange = this.getNormalizedSelectedRange(
      params.defaultSelectedRange,
      params.hideAllTimeRangeOption,
    );
    const normalizedDefaultStartDate = params.defaultStartDate ? startOfDay(params.defaultStartDate) : null;
    const normalizedDefaultEndDate = params.defaultEndDate ? endOfDay(params.defaultEndDate) : null;

    if (!params.entityId || !params.userId) {
      return {
        endDate: normalizedDefaultEndDate,
        filterState: null,
        selectedRange: normalizedDefaultSelectedRange,
        startDate: normalizedDefaultStartDate,
      };
    }

    const storedState = this.getState(params.entityType, params.entityId, params.userId);

    if (!storedState || !storedState.startDate || !storedState.endDate) {
      return {
        endDate: normalizedDefaultEndDate,
        filterState: null,
        selectedRange: normalizedDefaultSelectedRange,
        startDate: normalizedDefaultStartDate,
      };
    }

    const normalizedSelectedRange = this.getNormalizedSelectedRange(
      storedState.selectedRange,
      params.hideAllTimeRangeOption,
    );
    const { end: normalizedEndDate, start: normalizedStartDate } = this.resolveStoredRange(
      normalizedSelectedRange,
      storedState.startDate,
      storedState.endDate,
    );

    return {
      endDate: normalizedEndDate,
      filterState: {
        endDate: normalizedEndDate,
        selectedRange: normalizedSelectedRange,
        startDate: normalizedStartDate,
      },
      selectedRange: normalizedSelectedRange,
      startDate: normalizedStartDate,
    };
  }

  public setState(state: IProfileDateFilterStoredState): void {
    if (!this.storage) {
      return;
    }

    const key = this.buildKey(state.entityType, state.entityId, state.userId);
    const payload: IStoredProfileDateFilterState = {
      endDate: state.endDate ? state.endDate.toISOString() : null,
      entityId: state.entityId,
      entityType: state.entityType,
      selectedRange: state.selectedRange,
      startDate: state.startDate ? state.startDate.toISOString() : null,
      userId: state.userId,
    };

    this.storage.setItem(this.buildStorageKey(key), JSON.stringify(payload));
  }

  private buildKey(entityType: TProfileDateFilterEntityType, entityId: string, userId: string): string {
    return `${entityType}:${entityId}:${userId}`;
  }

  private buildStorageKey(key: string): string {
    return `${this.storagePrefix}:${key}`;
  }

  private getNormalizedSelectedRange(range: EDateRangeType, hideAllTimeRangeOption: boolean): EDateRangeType {
    if (hideAllTimeRangeOption && range === EDateRangeType.allTime) {
      return EDateRangeType.last30Days;
    }

    return range;
  }

  // A preset's stored dates are those of the day it was picked. The picker recomputes the preset
  // from today, so the page has to as well, or it keeps requesting that day's window.
  private resolveStoredRange(selectedRange: EDateRangeType, startDate: Date, endDate: Date): IDateRangeBounds {
    if (selectedRange === EDateRangeType.custom || selectedRange === EDateRangeType.allTime) {
      return { end: endOfDay(endDate), start: startOfDay(startDate) };
    }

    return calculatePredefinedDateRange(selectedRange, null);
  }

  private isStoredProfileDateFilterState(value: unknown): value is IStoredProfileDateFilterState {
    if (!value || typeof value !== 'object') {
      return false;
    }

    const state = value as IStoredProfileDateFilterState;
    const validEntityTypes: TProfileDateFilterEntityType[] = ['campaign', 'creator', 'creatorDeliverables', 'customer'];
    const validRanges: EDateRangeType[] = Object.values(EDateRangeType);

    return (
      (typeof state.endDate === 'string' || state.endDate === null) &&
      typeof state.entityId === 'string' &&
      validEntityTypes.includes(state.entityType) &&
      validRanges.includes(state.selectedRange) &&
      (typeof state.startDate === 'string' || state.startDate === null) &&
      typeof state.userId === 'string'
    );
  }

  private parseDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }

    const parsedDate = new Date(value);

    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate;
  }

  private safeParse(rawState: string): unknown {
    try {
      return JSON.parse(rawState) as unknown;
    } catch {
      return null;
    }
  }
}
